/**
 * Phase 20 — one campaign group's auto-send row: the group (its master
 * config's name and colour, how many containers run under it), today's fact,
 * its states (20/10/2026 — Off · Simulated · Live: Live asks first, real
 * SMS; Simulated decides on the real containers and sends nothing; OFF only
 * makes its drafts wait for a person), Resume while its stop-loss halts it, and its settings.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Play, SlidersHorizontal } from 'lucide-react';
import { autoSendModeOf, type DropGenGroupSendStatus, type DropGenServerSendModes } from '@trafficpulse/shared';

import { Button, SegmentedPills } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import { AutoSendConfirmDialog } from '../AutoSendConfirmDialog';
import { type AutoSendTone, groupFact } from '../autosend-fact';
import type { AutoSendLayer } from '../settings/autosend-draft';
import { groupLayer } from './group-rows';
import { modeOptions, useModeRights } from './mode-options';
import { useGroupSwitch } from './use-group-switch';

export const TONE: Record<AutoSendTone, string> = {
  off: 'text-muted-foreground',
  on: 'text-foreground',
  shadow: 'font-medium text-warning',
  halted: 'font-medium text-warning',
  stuck: 'font-medium text-destructive',
  blocked: 'text-muted-foreground',
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;


export const GroupSendRow: FC<{
  plan: DropGenPlanDetail;
  group: DropGenGroupSendStatus;
  server: DropGenServerSendModes | undefined;
  onSettings: (layer: AutoSendLayer) => void;
}> = ({ plan, group, server, onSettings }) => {
  const sw = useGroupSwitch(plan.countryKey, group);
  const fact = groupFact(group, plan.recipientTimezone, server);
  const current = autoSendModeOf(group.config);
  const options = modeOptions(useModeRights(), current);
  return (
    <li className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-3 py-2.5 sm:grid-cols-[auto_minmax(0,15rem)_minmax(0,1fr)_auto]" data-testid={`autosend-group-${group.masterId}`}>
      <span className={sw.canSwitch ? '' : 'pointer-events-none opacity-50'} aria-label={`Auto-send for ${group.name}`}>
        <SegmentedPills options={options} value={current} onChange={sw.pick} className="[&_button]:h-6 [&_button]:px-2 [&_button]:text-[11px]" />
      </span>
      <span className="flex min-w-0 items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: group.color }} aria-hidden />
        <span className="truncate font-medium">{group.name}</span>
        <span className="shrink-0 text-muted-foreground">{plural(group.containerCount, 'container')}</span>
      </span>
      <span className={`col-start-2 truncate sm:col-start-auto ${TONE[fact.tone]}`} title={group.haltedToday ? group.config.haltReason ?? undefined : undefined}>
        {fact.tone === 'on' ? <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-success align-middle" aria-hidden /> : null}
        {fact.tone === 'shadow' ? <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-warning align-middle" aria-hidden /> : null}
        {fact.text}
      </span>
      <span className="col-start-3 row-start-1 flex items-center gap-1.5 sm:col-start-auto sm:row-start-auto">
        {group.haltedToday && sw.canResume ? (
          <Button size="sm" variant="outline" loading={sw.resuming} onClick={sw.resume}><Play className="mr-1 h-3.5 w-3.5" />Resume</Button>
        ) : null}
        <button
          type="button"
          onClick={() => onSettings(groupLayer(group))}
          aria-label={`Auto-send settings of ${group.name}`}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/60 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
        </button>
      </span>
      <AutoSendConfirmDialog open={sw.confirming} plan={plan} layer={groupLayer(group)} today={group.sendToday} mode={sw.confirmMode} busy={sw.busy} onConfirm={sw.confirm} onCancel={sw.cancel} />
    </li>
  );
};
